'use client';

import classNames from 'classnames';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import XLight from '@/assets/xLight.svg';
import { useTranslations } from 'next-intl';
import tailwindConfig from '@/tailwind.screens';

type SearchFilterDateRangeType = {
  value: DateRange | undefined;
  className?: string;
  onChange: (value: DateRange | undefined) => void;
  onClear?: () => void;
};

const firstPresetYear = 2023;
const currentYear = new Date().getFullYear();
const yearPresets = Array.from({ length: Math.max(currentYear - firstPresetYear + 1, 0) }, (_, index) =>
  String(firstPresetYear + index),
);
const presets = ['Last 7 days', 'Last 30 days', ...yearPresets.reverse()];

const getMonthDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1);

const isSameMonth = (date: Date, otherDate: Date) =>
  date.getFullYear() === otherDate.getFullYear() && date.getMonth() === otherDate.getMonth();

const isSameDay = (date: Date, otherDate: Date) =>
  date.getFullYear() === otherDate.getFullYear() &&
  date.getMonth() === otherDate.getMonth() &&
  date.getDate() === otherDate.getDate();

const dayPickerClassNames = {
  months: 'flex gap-6',
  month: 'w-[288px]',
  month_caption:
    'mb-3 flex h-10 items-center justify-center rounded-md  text-sm font-medium text-slate-600 [&_.rdp-chevron]:ml-1 [&_.rdp-chevron]:!fill-gray-600  [&_.rdp-dropdown]:!p-2',
  nav: 'absolute inset-x-0 pr-3 pl-2 top-4 flex justify-between ',
  button_previous:
    'flex h-8 w-8 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 [&>.rdp-chevron]:!fill-gray-600',
  button_next:
    'flex h-8 w-8 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 [&>.rdp-chevron]:!fill-gray-600',
  weekdays: 'grid grid-cols-7 border-b border-slate-300 pb-2 text-sm font-normal text-slate-500',
  weekday: 'text-center font-normal',
  week: 'grid grid-cols-7 mt-0.5',
  day: 'relative h-10 w-10 text-sm text-slate-700',
  day_button: 'h-10 w-10 rounded-full transition-colors text-sm hover:bg-gray-100',
  outside: 'text-slate-400 opacity-50',
  today: 'font-medium text-blue-600',
  selected: '[&_button]:bg-gray-100 [&_button]:text-slate-700 [&_button]:rounded-full',
  range_start:
    'rounded-l-full bg-gray-100 text-white [&>button]:rounded-r-none [&>button]:!rounded-l-full hover:[&>button]:bg-gray-700 [&>button]:bg-gray-500 [&>button]:text-white',
  range_middle: 'rounded-none bg-gray-100 text-slate-700 [&>button]:rounded-none',
  range_end:
    'rounded-r-full bg-gray-100 text-white [&>button]:rounded-l-none [&>button]:!rounded-r-full [&>button]:bg-gray-500 hover:[&>button]:bg-gray-700 [&>button]:text-white',
  disabled: 'text-slate-300',
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatRangeLabel = (range: DateRange | undefined) => {
  if (range?.from && range?.to) {
    return `${formatDate(range.from)}  →  ${formatDate(range.to)}`;
  }

  if (range?.from) {
    return `${formatDate(range.from)}  →`;
  }

  if (range?.to) {
    return `→  ${formatDate(range.to)}`;
  }

  return '';
};

const getPresetRange = (preset: string, today = new Date()): DateRange | undefined => {
  const presetYear = Number(preset);

  if (Number.isInteger(presetYear) && presetYear >= firstPresetYear && presetYear <= today.getFullYear()) {
    return {
      from: new Date(presetYear, 0, 1),
      to: new Date(presetYear, 11, 31),
    };
  }

  if (preset === 'Last 7 days') {
    return {
      from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6),
      to: today,
    };
  }

  if (preset === 'Last 30 days') {
    return {
      from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29),
      to: today,
    };
  }

  return undefined;
};

const getPresetLabel = (range: DateRange | undefined) => {
  if (!range?.from || !range?.to) {
    return '';
  }

  const { from, to } = range;

  return (
    presets.find((preset) => {
      const presetRange = getPresetRange(preset);

      return presetRange?.from && presetRange.to && isSameDay(from, presetRange.from) && isSameDay(to, presetRange.to);
    }) ?? ''
  );
};

const SearchFilterDateRange = ({ value, className, onChange, onClear }: SearchFilterDateRangeType) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<DateRange | undefined>(value);
  const [numberOfMonths, setNumberOfMonths] = useState(2);
  const [leftMonth, setLeftMonth] = useState(getMonthDate(value?.from ?? new Date()));
  const [rightMonth, setRightMonth] = useState(addMonths(value?.from ?? new Date(), 1));

  const { refs, floatingStyles, context, placement } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 16 })],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useFocus(context),
    useClick(context, { toggle: false }),
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ]);

  useEffect(() => {
    if (open) {
      const initialMonth = getMonthDate(value?.from ?? new Date());
      const initialRightMonth =
        value?.to && value.from && !isSameMonth(value.from, value.to)
          ? getMonthDate(value.to)
          : addMonths(initialMonth, 1);
      setDraftValue(value);
      setLeftMonth(initialMonth);
      setRightMonth(initialRightMonth);
    }
  }, [open, value]);

  useEffect(() => {
    const onResize = () => {
      setNumberOfMonths(window.innerWidth >= parseInt(tailwindConfig.screens.md, 10) ? 2 : 1);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const basePlacement = placement.includes('-') ? placement.split('-')[0] : placement;
  const closedAnimation = basePlacement === 'top' ? { opacity: 0, y: 5 } : { opacity: 0, y: -5 };
  const displayValue = getPresetLabel(value) || formatRangeLabel(value);
  const placeholder = 'Select date range';

  const selectPreset = (preset: string) => {
    const range = getPresetRange(preset);

    if (!range) {
      return;
    }

    onChange(range);
    setOpen(false);
  };

  const handleCancel = () => {
    setDraftValue(value);
    setOpen(false);
  };

  const handleSelect = () => {
    onChange(draftValue);
    setOpen(false);
  };

  return (
    <div className={classNames('relative w-full', className)}>
      <div
        ref={refs.setReference}
        role="button"
        tabIndex={0}
        aria-label={displayValue || placeholder}
        className={classNames(
          'flex h-10 w-full cursor-pointer items-center rounded-full border border-black/10 bg-white px-4 text-sm font-medium outline-none',
          displayValue ? 'text-slate-900' : 'text-slate-400',
        )}
        {...getReferenceProps({
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen(true);
            }

            if (event.key === 'Escape') {
              setOpen(false);
            }
          },
        })}
      >
        {displayValue || placeholder}
      </div>

      {(value?.from || value?.to) && (
        <button
          type="button"
          onClick={() => {
            onChange(undefined);
            onClear?.();
          }}
          className="absolute top-2 right-2 cursor-pointer rounded-full p-1 text-sm hover:bg-gray-100 focus:ring-2 focus:ring-gray-300 focus:outline-none"
        >
          <XLight className="h-4 w-4" />
        </button>
      )}

      <AnimatePresence>
        {open && (
          <div ref={refs.setFloating} style={floatingStyles} className="z-50" {...getFloatingProps()}>
            <motion.div
              className="flex overflow-hidden rounded-md border border-slate-300 bg-white shadow-lg"
              variants={{
                closed: closedAnimation,
                open: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.4,
                  },
                },
              }}
              initial="closed"
              exit="closed"
              animate={open ? 'open' : 'closed'}
            >
              <div className="w-[104px] border-r border-slate-200 py-3">
                {presets.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => selectPreset(preset)}
                    className="block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100"
                  >
                    {preset}
                  </button>
                ))}
              </div>

              <div className="flex flex-col">
                {numberOfMonths === 1 ? (
                  <DayPicker
                    animate
                    captionLayout="dropdown"
                    mode="range"
                    numberOfMonths={1}
                    month={leftMonth}
                    onMonthChange={setLeftMonth}
                    selected={draftValue}
                    onSelect={setDraftValue}
                    startMonth={new Date(2023, 0)}
                    reverseYears
                    classNames={dayPickerClassNames}
                    className="p-3"
                  />
                ) : (
                  <div className="flex">
                    <DayPicker
                      animate
                      captionLayout="dropdown"
                      mode="range"
                      numberOfMonths={1}
                      month={leftMonth}
                      onMonthChange={setLeftMonth}
                      selected={draftValue}
                      onSelect={setDraftValue}
                      startMonth={new Date(2023, 0)}
                      reverseYears
                      classNames={dayPickerClassNames}
                      className="p-3"
                    />
                    <DayPicker
                      animate
                      captionLayout="dropdown"
                      mode="range"
                      numberOfMonths={1}
                      month={rightMonth}
                      onMonthChange={setRightMonth}
                      selected={draftValue}
                      onSelect={setDraftValue}
                      startMonth={new Date(2023, 0)}
                      reverseYears
                      classNames={dayPickerClassNames}
                      className="p-3"
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="cursor-pointer rounded-full px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
                  >
                    {t('general.$cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSelect}
                    className="cursor-pointer rounded-full bg-black px-4 py-2 text-sm font-medium text-white transition-colors"
                  >
                    {t('general.$select')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchFilterDateRange;
